import { useEffect, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';

import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from 'components/ui/sheet';
import { useResponsiveLayout } from 'hooks/useResponsiveLayout';

type SnapshotEditorShellProps = {
  open: boolean;
  title: string;
  description: string;
  dirty?: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  maxWidthClassName?: string;
};

export type SnapshotApiError = {
  message: string;
  fields: Record<string, string>;
  status: number | null;
};

export const readSnapshotApiError = (error: unknown): SnapshotApiError => {
  if (error && typeof error === 'object') {
    const response = (error as {
      response?: {
        status?: number;
        data?: { detail?: string; errors?: Record<string, string[] | string> };
      };
    }).response;
    const fields = Object.fromEntries(
      Object.entries(response?.data?.errors ?? {}).map(([field, messages]) => [
        field,
        Array.isArray(messages) ? messages[0] ?? '' : messages,
      ]),
    );
    return {
      message: response?.data?.detail || 'We could not save those changes. Your draft is still here.',
      fields,
      status: response?.status ?? null,
    };
  }
  return {
    message: 'We could not save those changes. Your draft is still here.',
    fields: {},
    status: null,
  };
};

export const SnapshotEditorShell = ({
  open,
  title,
  description,
  dirty = false,
  onOpenChange,
  children,
  maxWidthClassName = 'max-w-[640px]',
}: SnapshotEditorShellProps) => {
  const { isMobile } = useResponsiveLayout();
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  useEffect(() => {
    if (open) setConfirmDiscard(false);
  }, [open]);

  const requestClose = () => {
    if (dirty) {
      setConfirmDiscard(true);
      return;
    }
    onOpenChange(false);
  };

  const content = (
    <>
      <button
        type="button"
        className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full outline-none hover:bg-black/5 focus-visible:ring-2 focus-visible:ring-foundation-base-darkbase-dark-500"
        onClick={requestClose}
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="pr-12">
        <h2 className="font-headings-h2 text-2xl font-normal tracking-[-0.03em] text-foundation-base-darkbase-dark-500 [overflow-wrap:anywhere]">{title}</h2>
        <p className="mt-1 text-sm leading-6 text-muted-foreground [overflow-wrap:anywhere]">{description}</p>
      </div>
      <div className="mt-6">{children}</div>
      {confirmDiscard ? (
        <div role="alertdialog" aria-label="Discard unsaved changes" className="mt-5 rounded-2xl border border-foundation-orange-200 bg-foundation-orange-50 p-4">
          <p className="text-sm font-semibold text-foundation-base-darkbase-dark-500">Discard your unsaved changes?</p>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">You can keep editing or close without saving.</p>
          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" size="auto" className="min-h-11" onClick={() => setConfirmDiscard(false)}>Keep editing</Button>
            <Button type="button" variant="secondary" size="auto" className="min-h-11" onClick={() => onOpenChange(false)}>Discard changes</Button>
          </div>
        </div>
      ) : null}
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(next) => { if (!next) requestClose(); }}>
        <SheetContent side="bottom" className="max-h-[92dvh] overflow-y-auto pb-[max(1.5rem,env(safe-area-inset-bottom))] [&>button]:hidden">
          <SheetTitle className="sr-only">{title}</SheetTitle>
          <SheetDescription className="sr-only">{description}</SheetDescription>
          {content}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) requestClose(); }}>
      <DialogContent className={`${maxWidthClassName} max-h-[90dvh] overflow-y-auto rounded-3xl p-6 sm:p-7 [&>button]:hidden`}>
        <DialogHeader className="sr-only">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
};
